/**
 * Decision to execution — the fetch half of L3 (DECISION-LATENCY PRD §5).
 * Arithmetic: decisionExecutionCore.ts. Judgment: commitmentState.ts.
 *
 * READ-ONLY. SELECTs only — no RPC, no insert, no update, no stored state.
 * Built as a service rather than a database view for the same reason L1 was
 * (this PRD allows either): the committed/announced judgment lives in one
 * place, commitmentState.ts, and a view would need a second copy of it.
 *
 * The chain, per plan cycle in the workspace:
 *
 *   plan_cycles ─→ plan_of_record (authorized_at, plan_version_id, status)
 *                      └─→ initiatives.item_id   the commitments decided
 *   plan_resource_claims (plan_cycle_id, item_id) the binding history
 *   items.work_type ─→ execution recipe          the requirement
 *
 * "Decided at" for a commitment is the first authorization in its cycle whose
 * plan carried it. The commitments counted are the ones on the cycle's current
 * (active) authorized plan.
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import { listClaimsForCycles } from './resourceClaimsService';
import { listExecutionRecipes } from './executionRecipeService';
import { fetchWorkTypes, requirementForRecipe, resolveRecipeForWorkType } from './commitmentStateService';
import { HOURS_PER_CAPACITY_UNIT } from './itemToPlanningAdapter';
import type { ResourceRequirementLine } from './commitmentState';
import {
  buildCycleExecution,
  buildExecutionTrend,
  type CommitmentExecutionInput,
  type CycleExecution,
  type ExecutionTrend,
  type TimedClaim,
} from './decisionExecutionCore';

export interface DecisionExecutionReport {
  trend: ExecutionTrend;
  error?: string;
}

const EMPTY: DecisionExecutionReport = { trend: buildExecutionTrend([]) };

export async function fetchDecisionExecution(
  workspaceId: string,
  options: { since?: string } = {},
): Promise<DecisionExecutionReport> {
  const db = getTypedSupabaseClient();

  const { data: cycles, error: cycleError } = await db
    .from('plan_cycles')
    .select('id, name')
    .eq('workspace_id', workspaceId);
  if (cycleError) return { ...EMPTY, error: cycleError.message };
  if (!cycles || cycles.length === 0) return EMPTY;
  const nameById = new Map(cycles.map((c) => [c.id, c.name]));

  const { data: records, error: porError } = await db
    .from('plan_of_record')
    .select('plan_cycle_id, plan_version_id, authorized_at, status')
    .in('plan_cycle_id', cycles.map((c) => c.id));
  if (porError) return { ...EMPTY, error: porError.message };

  const byCycle = new Map<string, { authorizedAt: string; versionId: string }[]>();
  const activeVersion = new Map<string, string>();
  for (const r of records ?? []) {
    if (!r.plan_cycle_id || !r.plan_version_id || !r.authorized_at) continue;
    const list = byCycle.get(r.plan_cycle_id) ?? [];
    list.push({ authorizedAt: r.authorized_at, versionId: r.plan_version_id });
    byCycle.set(r.plan_cycle_id, list);
    if (r.status === 'active') activeVersion.set(r.plan_cycle_id, r.plan_version_id);
  }
  const cycleIds = [...activeVersion.keys()].filter((id) => {
    if (!options.since) return true;
    const first = firstAuthorization(byCycle.get(id) ?? []);
    return first !== null && first >= options.since;
  });
  if (cycleIds.length === 0) return EMPTY;

  const versionIds = [...new Set(cycleIds.flatMap((id) => (byCycle.get(id) ?? []).map((r) => r.versionId)))];
  const [initiativesRes, claimsRes, recipesRes] = await Promise.all([
    db.from('initiatives').select('plan_version_id, item_id, title').in('plan_version_id', versionIds),
    listClaimsForCycles(cycleIds),
    listExecutionRecipes(workspaceId),
  ]);
  if (initiativesRes.error) return { ...EMPTY, error: initiativesRes.error.message };
  if (claimsRes.error) return { ...EMPTY, error: claimsRes.error };

  const itemsByVersion = new Map<string, Map<string, string | null>>();
  for (const row of initiativesRes.data ?? []) {
    if (!row.item_id || !row.plan_version_id) continue;
    const m = itemsByVersion.get(row.plan_version_id) ?? new Map<string, string | null>();
    if (!m.has(row.item_id)) m.set(row.item_id, row.title ?? null);
    itemsByVersion.set(row.plan_version_id, m);
  }

  const allItemIds = [...new Set(cycleIds.flatMap((id) => [...(itemsByVersion.get(activeVersion.get(id)!)?.keys() ?? [])]))];
  const workTypes = allItemIds.length > 0 ? await fetchWorkTypes(allItemIds) : { byItemId: new Map<string, string | null>() };
  if ('error' in workTypes && workTypes.error) return { ...EMPTY, error: workTypes.error };

  const requirementCache = new Map<string, ResourceRequirementLine[]>();
  const requirementFor = (itemId: string): ResourceRequirementLine[] => {
    const wt = workTypes.byItemId.get(itemId);
    if (!wt) return [];
    if (!requirementCache.has(wt)) {
      requirementCache.set(
        wt,
        requirementForRecipe(resolveRecipeForWorkType(recipesRes.data, wt), HOURS_PER_CAPACITY_UNIT),
      );
    }
    return requirementCache.get(wt)!;
  };

  const claimsByKey = new Map<string, TimedClaim[]>();
  for (const row of claimsRes.rows) {
    const key = `${row.plan_cycle_id}:${row.item_id}`;
    const list = claimsByKey.get(key) ?? [];
    list.push({
      id: row.id,
      claimKind: row.claim_kind as TimedClaim['claimKind'],
      status: row.status as TimedClaim['status'],
      resourceType: row.resource_type,
      declaredHours: row.declared_hours == null ? null : Number(row.declared_hours),
      declaredAmount: row.declared_amount == null ? null : Number(row.declared_amount),
      currency: row.currency,
      createdAt: row.created_at,
      withdrawnAt: row.withdrawn_at,
      supersededByClaimId: row.superseded_by_claim_id,
    });
    claimsByKey.set(key, list);
  }

  const result: CycleExecution[] = cycleIds.map((cycleId) => {
    const auths = (byCycle.get(cycleId) ?? []).slice().sort((a, b) => a.authorizedAt.localeCompare(b.authorizedAt));
    const current = itemsByVersion.get(activeVersion.get(cycleId)!) ?? new Map<string, string | null>();
    const commitments: CommitmentExecutionInput[] = [...current.entries()].map(([itemId, title]) => {
      const firstCarrying = auths.find((a) => itemsByVersion.get(a.versionId)?.has(itemId));
      return {
        itemId,
        title,
        decidedAt: (firstCarrying ?? auths[auths.length - 1]).authorizedAt,
        requirements: requirementFor(itemId),
        claims: claimsByKey.get(`${cycleId}:${itemId}`) ?? [],
      };
    });
    return buildCycleExecution({
      planCycleId: cycleId,
      name: nameById.get(cycleId) ?? 'Untitled cycle',
      authorizedAt: auths[0].authorizedAt,
      commitments,
    });
  });

  return {
    trend: buildExecutionTrend(result),
    error: recipesRes.error ?? undefined,
  };
}

function firstAuthorization(list: { authorizedAt: string }[]): string | null {
  return list.reduce<string | null>((min, r) => (min === null || r.authorizedAt < min ? r.authorizedAt : min), null);
}
